import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import type {
    AirBnBIntegrationConfig,
    PropertyModules,
} from '../../../../../packages/entities/Property';
import { getAvailableModules, type ModuleDefinition } from '../modules/registry';

const SENSITIVE_FIELDS = ['cookie', 'token'];

interface ModuleConfigurationProps {
    modules: PropertyModules;
    onChange: (modules: PropertyModules) => void;
    disabled?: boolean;
    focusModuleId?: string;
    onModuleSelect?: (moduleId: string) => void;
    propertyCountry: string;
}

const ModuleConfiguration = ({
    modules,
    onChange,
    disabled,
    focusModuleId,
    onModuleSelect,
    propertyCountry,
}: ModuleConfigurationProps) => {
    const availableModules = getAvailableModules(propertyCountry);
    const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());
    const [selectedModuleId, setSelectedModuleId] = useState<string | null>(
        availableModules[0]?.id ?? null
    );

    // When focusModuleId changes, select that module
    useEffect(() => {
        if (focusModuleId && availableModules.some(m => m.id === focusModuleId)) {
            setSelectedModuleId(focusModuleId);
        }
    }, [focusModuleId, availableModules]);

    const handleModuleSelect = (moduleId: string) => {
        setSelectedModuleId(moduleId);
        onModuleSelect?.(moduleId);
    };

    const toggleFieldVisibility = (fieldKey: string) => {
        setVisibleFields(prev => {
            const next = new Set(prev);
            if (next.has(fieldKey)) {
                next.delete(fieldKey);
            } else {
                next.add(fieldKey);
            }
            return next;
        });
    };

    const handleModuleToggle = (moduleId: string) => {
        const currentConfig = modules[moduleId] as
            | Record<string, unknown>
            | undefined;
        const isEnabled =
            currentConfig &&
            typeof currentConfig === 'object' &&
            'enabled' in currentConfig
                ? currentConfig.enabled
                : false;

        const updatedModules = { ...modules } as Record<string, unknown>;
        if (isEnabled) {
            // Keep the configuration but set enabled to false
            updatedModules[moduleId] = { ...currentConfig, enabled: false };
        } else {
            // Enable with default configuration
            const moduleDefinition = availableModules.find(
                m => m.id === moduleId
            );
            if (moduleDefinition) {
                const defaultConfig: Record<string, unknown> = {
                    enabled: true,
                };
                // Initialize required fields with empty strings
                moduleDefinition.configFields.forEach(field => {
                    if (field.required && field.type !== 'boolean') {
                        defaultConfig[field.key] = '';
                    }
                });
                updatedModules[moduleId] = {
                    ...currentConfig,
                    ...defaultConfig,
                };
            }
        }
        onChange(updatedModules as PropertyModules);
    };

    const handleFieldChange = (
        moduleId: string,
        fieldKey: string,
        value: unknown
    ) => {
        const currentConfig =
            (modules[moduleId] as unknown as Record<string, unknown>) ?? {};
        const updatedModules = {
            ...modules,
            [moduleId]: {
                ...currentConfig,
                [fieldKey]: value,
            },
        } as PropertyModules;
        onChange(updatedModules);
    };

    const isModuleEnabled = (moduleId: string): boolean => {
        const config = modules[moduleId];
        return Boolean(
            config &&
                typeof config === 'object' &&
                'enabled' in config &&
                config.enabled === true
        );
    };

    const getFieldValue = (moduleId: string, fieldKey: string): string => {
        const config = modules[moduleId] as unknown as
            | Record<string, unknown>
            | undefined;
        if (!config || typeof config !== 'object') return '';
        const value = config[fieldKey];
        return typeof value === 'string' ? value : '';
    };

    const getBooleanFieldValue = (moduleId: string, fieldKey: string): boolean => {
        const config = modules[moduleId] as unknown as
            | Record<string, unknown>
            | undefined;
        if (!config || typeof config !== 'object') return false;
        const value = config[fieldKey];
        return typeof value === 'boolean' ? value : false;
    };

    const getNumberFieldValue = (moduleId: string, fieldKey: string): string => {
        const config = modules[moduleId] as unknown as
            | Record<string, unknown>
            | undefined;
        if (!config || typeof config !== 'object') return '';
        const value = config[fieldKey];
        return typeof value === 'number' ? String(value) : '';
    };

    const renderSyncStatus = (moduleId: string) => {
        if (moduleId !== 'integration/airbnb') return null;

        const config = modules[moduleId] as AirBnBIntegrationConfig | undefined;
        if (!config?.enabled) return null;

        const { lastSyncAt, syncStatus, syncError } = config;

        return (
            <Box sx={{ mt: 2 }}>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#9ca3af',
                        mb: 1,
                    }}
                >
                    Sync Status
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                    {syncStatus && (
                        <Box
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                border: '2px solid',
                                borderColor:
                                    syncStatus === 'success'
                                        ? '#22c55e'
                                        : syncStatus === 'error'
                                          ? '#ef4444'
                                          : '#f59e0b',
                                backgroundColor:
                                    syncStatus === 'success'
                                        ? 'rgba(34, 197, 94, 0.1)'
                                        : syncStatus === 'error'
                                          ? 'rgba(239, 68, 68, 0.1)'
                                          : 'rgba(245, 158, 11, 0.1)',
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color:
                                    syncStatus === 'success'
                                        ? '#22c55e'
                                        : syncStatus === 'error'
                                          ? '#ef4444'
                                          : '#f59e0b',
                            }}
                        >
                            {syncStatus}
                        </Box>
                    )}
                    {lastSyncAt && (
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                color: '#9ca3af',
                            }}
                        >
                            Last sync: {new Date(lastSyncAt).toLocaleString()}
                        </Typography>
                    )}
                    {!syncStatus && !lastSyncAt && (
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                color: '#9ca3af',
                            }}
                        >
                            Not yet synced
                        </Typography>
                    )}
                </Stack>
                {syncError && (
                    <Alert
                        severity="error"
                        sx={{
                            mt: 1.5,
                            borderRadius: 0,
                            border: '2px solid #ef4444',
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            '& .MuiAlert-icon': {
                                color: '#ef4444',
                            },
                        }}
                    >
                        {syncError}
                    </Alert>
                )}
            </Box>
        );
    };

    const renderModuleFields = (module: ModuleDefinition) => {
        const enabled = isModuleEnabled(module.id);
        if (!enabled) return null;

        const textFieldSx = {
            '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                '& input': {
                    backgroundColor: '#0f0f23',
                },
                '& fieldset': {
                    borderWidth: '2px',
                    borderColor: '#374151',
                },
                '&:hover fieldset': {
                    borderWidth: '2px',
                    borderColor: '#6366f1',
                },
                '&.Mui-focused fieldset': {
                    borderWidth: '3px',
                    borderColor: '#6366f1',
                },
            },
            '& .MuiInputLabel-root': {
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.875rem',
                letterSpacing: '0.05em',
            },
            '& .MuiFormHelperText-root': {
                fontWeight: 500,
                fontSize: '0.75rem',
            },
        };

        return (
            <Stack spacing={2} sx={{ mt: 2 }}>
                {module.configFields.map(field => {
                    if (field.type === 'boolean') {
                        return (
                            <FormControlLabel
                                key={field.key}
                                control={
                                    <Checkbox
                                        checked={getBooleanFieldValue(
                                            module.id,
                                            field.key
                                        )}
                                        onChange={e =>
                                            handleFieldChange(
                                                module.id,
                                                field.key,
                                                e.target.checked
                                            )
                                        }
                                        disabled={disabled}
                                        sx={{
                                            color: '#374151',
                                            '&.Mui-checked': {
                                                color: '#6366f1',
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            {field.label}
                                        </Typography>
                                        {field.helpText && (
                                            <Typography
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: '0.75rem',
                                                    color: '#9ca3af',
                                                }}
                                            >
                                                {field.helpText}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                sx={{
                                    alignItems: 'flex-start',
                                    '& .MuiFormControlLabel-label': {
                                        mt: 0.5,
                                    },
                                }}
                            />
                        );
                    }

                    const isSensitive = SENSITIVE_FIELDS.includes(field.key);
                    const fieldId = `${module.id}-${field.key}`;
                    const isVisible = visibleFields.has(fieldId);
                    const isNumber = field.type === 'number';

                    return (
                        <TextField
                            key={field.key}
                            label={field.label}
                            type={
                                isSensitive && !isVisible
                                    ? 'password'
                                    : isNumber
                                      ? 'number'
                                      : field.type === 'email'
                                        ? 'email'
                                        : 'text'
                            }
                            fullWidth
                            value={isNumber
                                ? getNumberFieldValue(module.id, field.key)
                                : getFieldValue(module.id, field.key)}
                            onChange={e =>
                                handleFieldChange(
                                    module.id,
                                    field.key,
                                    isNumber
                                        ? (e.target.value === '' ? '' : Number(e.target.value))
                                        : e.target.value
                                )
                            }
                            disabled={disabled}
                            required={field.required}
                            placeholder={field.placeholder}
                            helperText={field.helpText}
                            InputProps={
                                isSensitive
                                    ? {
                                          endAdornment: (
                                              <InputAdornment position="end">
                                                  <IconButton
                                                      onClick={() =>
                                                          toggleFieldVisibility(
                                                              fieldId
                                                          )
                                                      }
                                                      edge="end"
                                                      sx={{
                                                          color: '#9ca3af',
                                                      }}
                                                  >
                                                      {isVisible ? (
                                                          <VisibilityOffIcon />
                                                      ) : (
                                                          <VisibilityIcon />
                                                      )}
                                                  </IconButton>
                                              </InputAdornment>
                                          ),
                                      }
                                    : undefined
                            }
                            sx={textFieldSx}
                        />
                    );
                })}
                {renderSyncStatus(module.id)}
            </Stack>
        );
    };

    if (availableModules.length === 0) {
        return (
            <Box
                sx={{
                    p: 2,
                    border: '2px solid #374151',
                    backgroundColor: '#0f0f23',
                    textAlign: 'center',
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 600,
                        color: '#9ca3af',
                        textTransform: 'uppercase',
                        fontSize: '0.875rem',
                        letterSpacing: '0.05em',
                    }}
                >
                    No modules available
                </Typography>
            </Box>
        );
    }

    const selectedModule = availableModules.find(m => m.id === selectedModuleId);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                border: '2px solid #374151',
                minHeight: 350,
            }}
        >
            {/* Sidebar - Module List */}
            <Box
                sx={{
                    width: { xs: '100%', sm: 280 },
                    maxHeight: { xs: 200, sm: 'none' },
                    flexShrink: 0,
                    borderRight: { xs: 'none', sm: '2px solid #374151' },
                    borderBottom: { xs: '2px solid #374151', sm: 'none' },
                    backgroundColor: '#0f0f23',
                    overflowY: 'auto',
                }}
            >
                {availableModules.map(module => {
                    const enabled = isModuleEnabled(module.id);
                    const isSelected = selectedModuleId === module.id;
                    return (
                        <Box
                            key={module.id}
                            onClick={() => handleModuleSelect(module.id)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: 1.5,
                                cursor: 'pointer',
                                borderBottom: '1px solid #374151',
                                backgroundColor: isSelected
                                    ? '#1a1a2e'
                                    : 'transparent',
                                borderLeft: isSelected
                                    ? '3px solid #6366f1'
                                    : '3px solid transparent',
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    backgroundColor: '#1a1a2e',
                                },
                            }}
                        >
                            <Switch
                                data-testid={`module-switch-${module.id}`}
                                size="small"
                                checked={enabled}
                                disabled={disabled}
                                onClick={e => {
                                    e.stopPropagation();
                                    handleModuleToggle(module.id);
                                }}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#6366f1',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                        {
                                            backgroundColor: '#6366f1',
                                        },
                                }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.03em',
                                        color: enabled ? '#6366f1' : '#e5e7eb',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {module.name}
                                </Typography>
                            </Box>
                            {enabled && (
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: '#22c55e',
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                        </Box>
                    );
                })}
            </Box>

            {/* Content - Module Configuration */}
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: '#1a1a2e',
                    p: 2.5,
                    overflowY: 'auto',
                }}
            >
                {selectedModule ? (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    color: isModuleEnabled(selectedModule.id)
                                        ? '#6366f1'
                                        : '#e5e7eb',
                                }}
                            >
                                {selectedModule.name}
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    color: '#9ca3af',
                                    mt: 0.5,
                                }}
                            >
                                {selectedModule.description}
                            </Typography>
                        </Box>
                        {isModuleEnabled(selectedModule.id) ? (
                            renderModuleFields(selectedModule)
                        ) : (
                            <Box
                                sx={{
                                    p: 3,
                                    border: '2px dashed #374151',
                                    textAlign: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        color: '#9ca3af',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    Enable this module to configure it
                                </Typography>
                            </Box>
                        )}
                    </>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 600,
                                color: '#9ca3af',
                                fontSize: '0.875rem',
                            }}
                        >
                            Select a module to configure
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ModuleConfiguration;
